import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import ActionCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/ActionCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import TimeCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/TimeCell";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getMembranesRowValues } from "@/encounters/components/EncounterLabor/utils/labor";

interface MembranesRowProps {
  time: string;
  observation: Observation;
  showNumbersOnly: boolean;
  showPreviousVersions?: boolean;
  isViewOnly?: boolean;
}

export default function MembranesRow({
  time,
  observation,
  showNumbersOnly,
  showPreviousVersions = true,
  isViewOnly = false,
}: MembranesRowProps) {
  const {
    pooling,
    nitrazine,
    fernResults,
    fluidColor,
    fluidColorOther,
    notes,
    hasAnswers,
    hasNotes,
  } = useMemo(() => getMembranesRowValues(observation), [observation]);

  const previousVersions = hasPreviousVersions(observation);

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <TimeCell time={time} rowSpan={1} isViewOnly={isViewOnly} observation={observation} />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Membranes:</TextWrapper>
            <List fz="sm" withPadding>
              {fluidColor && (
                <List.Item>
                  <strong>Fluid Color:</strong> {fluidColor}
                </List.Item>
              )}
              {fluidColorOther && (
                <List.Item>
                  <strong>Fluid Color Other:</strong> {fluidColorOther}
                </List.Item>
              )}
              {pooling && (
                <List.Item>
                  <strong>Pooling:</strong> {pooling}
                </List.Item>
              )}
              {nitrazine && (
                <List.Item>
                  <strong>Nitrazine:</strong> {nitrazine}
                </List.Item>
              )}
              {fernResults && (
                <List.Item>
                  <strong>Fern Results:</strong> {fernResults}
                </List.Item>
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            Membranes Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={MembranesRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
      {showPreviousVersions && !isViewOnly && <ActionCell rowSpan={1} observation={observation} />}
    </Table.Tr>
  );
}
